import { useState } from 'react';
import { Tabs, Row, Col, Select, Form } from 'antd';
import { ColumnsType } from 'antd/es/table'
import { ReModal, ReButton } from "@/components/refineElement";
import { getLangList } from '@/lib/i18n';
import { getOptionArray, getDataArray, selectFilterOption, sortColumn } from "@/lib/antdElementFunction";
import ArchivedConfig from "@/config/archived";
import YearSemesterToolBar from "@/components/YearSemesterToolBar";
import ResponsiveTable from '@/components/responsiveTable';
import ModalTranscriptType1 from '@/containers/transcript/ModalTranscriptType1';
import ModalTranscriptType2 from '@/containers/transcript/ModalTranscriptType2';
import { SelectedItemObjectType, ExamType, GradeType, ClassIntervalType, ClassType, StudentType } from './types';

const { getArchivedTranscript } = ArchivedConfig;

function ArchivedTranscript() {
  const [dataArray, setDataArray] = useState<ExamType[]>([]);
  const [examNameArray, setExamNameArray] = useState<string[]>([]);
  const [selectedItemObject, setSelectedItemObject] = useState<SelectedItemObjectType>({
    selectedExamObject: null,
    selectedClassTypeObject: null,
    selectedClassObject: null,
  });
  const [studentInfo, setStudentInfo] = useState({} as StudentType);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [yearSemesterForm] = Form.useForm();
  const { selectedExamObject, selectedClassTypeObject, selectedClassObject } = selectedItemObject;
  const semesterT: any = getLangList("semester");
  const studentListT: any = getLangList("studentList");
  const transcriptT: any = getLangList("transcript");
  const buttonT: any = getLangList("button");

  const getColumns = () => {
    const fixedColumns: ColumnsType<StudentType> = [
      {
        title: studentListT.SEAT_NO,
        dataIndex: "cramSeat",
        sorter: (a: any, b: any) => sortColumn(a.cramSeat, b.cramSeat),
        sortDirections: ['ascend', 'descend', 'ascend'],
        defaultSortOrder: 'ascend',
      },
      { title: studentListT.ACCOUNT, dataIndex: "username" },
      { title: studentListT.CN_NAME, dataIndex: "nameCn" },
      { title: studentListT.EN_NAME, dataIndex: "nameEn" },
    ];
    const actionColumns = [
      {
        title: studentListT.OPERATE,
        dataIndex: "action",
        render: (text: any, record: any) => (
          <ReButton iconName='book' onClick={clickTranscript(record)}>
            {transcriptT.TRANSCRIPT}
          </ReButton>
        ),
      }
    ];

    const dynamicColumns = selectedClassObject?.students?.[0]?.grades?.map((gradeItem: GradeType) => ({ title: gradeItem.subject, dataIndex: gradeItem.subject, width: 150 })) ?? [];

    return [...fixedColumns, ...dynamicColumns, ...actionColumns];
  }

  const getArchivedTranscriptFunction = () => {
    const { year, semesterName } = yearSemesterForm.getFieldsValue();

    getArchivedTranscript({ year, semester: semesterName }).then((res: any) => {
      const dataArray = res?.value ?? [];
      const examNameArray = dataArray.map((item: ExamType) => item.examName); // 國文, 英文, 數學, 社會, 自然...

      setExamNameArray(examNameArray);
      setDataArray(dataArray);
    });
  };

  const getSubjectListForExam = (subjectList: any) => {
    const subjectListForExam: any = {};

    subjectList.forEach((subject: any) => {
      const { subjectType } = subject;
      const subjectTypeArray = subjectListForExam?.[subjectType] ?? [];
      subjectListForExam[subjectType] = [ ...subjectTypeArray, subject];
    });

    return subjectListForExam;
  }

  const onChangeExamName = (examName: string) => {
    const examObject = dataArray?.find((item: ExamType) => item.examName === examName) ?? null;
    const classTypeObject = examObject?.classTypes?.[0] ?? null;
    const classObject = classTypeObject?.classes?.[0] ?? null;

    setSelectedItemObject({
      selectedExamObject: examObject,
      selectedClassTypeObject: classTypeObject,
      selectedClassObject: classObject,
    });
  };

  const clickTranscript = (data: StudentType) => (e: any) => {
    const formatedData = { ...data };
    const { grades = [] } = data;

    grades.forEach((grade: GradeType) => (formatedData[grade.subjectId] = grade.score));
    e.stopPropagation();
    setStudentInfo(formatedData);
    setIsModalVisible(!isModalVisible);
  };

  return (
    <>
      <Row justify="center">
        <Col>
          <YearSemesterToolBar
            yearSemesterForm={yearSemesterForm}
            onChangeSemester={() => {
              getArchivedTranscriptFunction();
              setSelectedItemObject({
                selectedExamObject: null,
                selectedClassTypeObject: null,
                selectedClassObject: null,
              });
            }}
          />
        </Col>

        <Col>
          {`${semesterT.EXAM} : `}
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={(input: any, option: any) => selectFilterOption(input, option)}
            style={{ width: '200px' }}
            value={selectedExamObject?.examName || null}
            onChange={onChangeExamName}
            disabled={examNameArray.length === 0}
          >
            {getOptionArray(getDataArray(examNameArray, '', ''))}
          </Select>
        </Col>
      </Row>

      <Row gutter={[0, 8]}>
        <Col span={24}>
          <Tabs
            size="large"
            type="card"
            activeKey={selectedClassTypeObject?.classType}
            onChange={(classType: string) => {
              const selectedClassTypeObject = selectedExamObject?.classTypes?.find((classTypeItem: ClassIntervalType) => classTypeItem.classType === classType) ?? null;
              const selectedClassObject = selectedClassTypeObject?.classes?.[0] ?? null;

              setSelectedItemObject({ ...selectedItemObject, selectedClassTypeObject, selectedClassObject });
            }}
            items={dataArray?.find((examItem) => examItem.examName === selectedExamObject?.examName)?.classTypes?.map((classTypeItem: ClassIntervalType) => ({
              label: classTypeItem.classType,
              key: classTypeItem.classType,
              children: (
                <Tabs
                  size="large"
                  tabPosition="left"
                  activeKey={selectedClassObject?.className}
                  onChange={(className: string) => setSelectedItemObject({
                    ...selectedItemObject,
                    selectedClassObject: selectedClassTypeObject?.classes?.find((classItem: ClassType) => classItem.className === className) ?? null,
                  })}
                  items={classTypeItem?.classes.map((classItem: ClassType) => ({
                    label: classItem.className,
                    key: classItem.className,
                    children: (
                      <ResponsiveTable
                        columns={getColumns()}
                        dataSource={classItem?.students?.map((student: StudentType) => {
                          const formatedStudent = { ...student };
                          const { grades = [] } = student;

                          grades.forEach((grade: GradeType) => (formatedStudent[grade.subject] = grade.score));
                          return formatedStudent;
                        })}
                      />
                    )
                  }))
                  }
                />
              )
            }))}
          />
        </Col>
      </Row>

      <ReModal
        title={transcriptT.TRANSCRIPT}
        onCancel={() => setIsModalVisible(!isModalVisible)}
        visible={isModalVisible}
        onOk={() => { }}
        CollectedForm={selectedClassObject?.template === 1 ? ModalTranscriptType1 : ModalTranscriptType2}
        formProps={{
          info: studentInfo,
          subjectListForExam: getSubjectListForExam(selectedClassObject?.subjectList ?? []),
          year: yearSemesterForm?.getFieldsValue()?.year ?? '',
          classInfo: {
            teacherName: selectedClassObject?.teacherName ?? '',
            className: selectedClassObject?.className ?? '',
            semesterName: yearSemesterForm?.getFieldsValue()?.semesterName ?? '',
          },
        }}
        width={'851px'}
        okText={buttonT.PRINT}
        Footer={null}
      />
    </>
  );
}

export default ArchivedTranscript;